import { motion } from 'framer-motion';
import { FEATURED, RECENT_SALES } from './data';

// HOME — Filofax estate-index card hero. NO parallax photo. Bone-paper plate with
// dossier metadata, three featured property index cards, and a brass marquee of recent closings.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#F4EFE5] text-[#0E1B2D]">
      {/* DOSSIER HERO — paper plate with hand-typed broker office details */}
      <section className="pt-36 pb-16 max-w-[1500px] mx-auto px-8 lg:px-14">
        <div className="grid lg:grid-cols-[1.7fr_1fr] gap-10 items-end pb-8 border-b-2 border-[#1F3A5F]">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B5527] mb-5">— Confidential Office Brief · Issue MMXXVI / 04 —</p>
            <h1 className="font-serif text-[64px] sm:text-[96px] lg:text-[140px] leading-[0.92] tracking-[-0.02em] text-[#1F3A5F]">
              Trophy<br />estates,<br /><span className="italic">brokered quietly.</span>
            </h1>
          </div>
          <div className="border-l border-[#1F3A5F]/30 pl-6 space-y-4 lg:max-w-sm">
            <div>
              <p className="font-mono text-[9px] tracking-[0.32em] uppercase text-[#1F3A5F]/55">In office</p>
              <p className="font-serif text-2xl text-[#1F3A5F]">11 listings · 3 reserved</p>
            </div>
            <div>
              <p className="font-mono text-[9px] tracking-[0.32em] uppercase text-[#1F3A5F]/55">Aggregate inventory value</p>
              <p className="font-serif text-2xl text-[#1F3A5F]">$612M · €138M</p>
            </div>
            <div>
              <p className="font-mono text-[9px] tracking-[0.32em] uppercase text-[#1F3A5F]/55">Closings · Trailing 24 mo.</p>
              <p className="font-serif text-2xl text-[#1F3A5F]">$284M placed</p>
            </div>
            <button onClick={() => setPage('inventory')} data-testid="ml-home-cta" className="mt-4 inline-block bg-[#1F3A5F] text-[#F4EFE5] px-7 py-3.5 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#7B5527] transition-colors">Review inventory →</button>
          </div>
        </div>
      </section>

      {/* THREE INDEX CARDS — featured listings as filofax cards stamped with reference codes */}
      <section className="max-w-[1500px] mx-auto px-8 lg:px-14 py-10">
        <div className="flex items-baseline justify-between mb-8">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#7B5527]">— Three to consider this week</p>
          <button onClick={() => setPage('inventory')} className="font-mono text-[10px] tracking-[0.32em] uppercase border-b border-[#1F3A5F] text-[#1F3A5F] hover:border-[#7B5527] hover:text-[#7B5527]">All inventory →</button>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {FEATURED.concat({ ref: 'ML-128', region: 'Pitkin County, CO', name: 'Maroon Bells Reserve', sqft: '19,200', acres: '36.0', beds: 11, baths: 14, ask: 'On request', img: 'https://images.pexels.com/photos/2724748/pexels-photo-2724748.jpeg?auto=compress&cs=tinysrgb&w=1400', status: 'available' }).map((p, i) => (
            <motion.article key={p.ref} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white border border-[#1F3A5F]/15 group">
              <div className="aspect-[4/5] overflow-hidden border-b border-[#1F3A5F]/15">
                <img src={p.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between border-b border-dashed border-[#1F3A5F]/20 pb-2.5 mb-3">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#7B5527]">{p.ref}</p>
                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#1F3A5F]/50">{p.region}</p>
                </div>
                <h3 className="font-serif italic text-2xl text-[#1F3A5F]">{p.name}</h3>
                <div className="grid grid-cols-3 gap-2 mt-4 mb-4 font-mono text-[10px] uppercase text-[#1F3A5F]/65 tracking-[0.18em]">
                  <span>{p.beds} BR</span><span>{p.baths} BA</span><span>{p.sqft} ft²</span>
                </div>
                <p className="font-serif text-xl text-[#7B5527]">{p.ask}</p>
                <button onClick={() => setPage('viewing')} className="mt-4 w-full font-mono text-[10px] tracking-[0.32em] uppercase border border-[#1F3A5F]/30 py-2.5 hover:bg-[#1F3A5F] hover:text-[#F4EFE5] hover:border-[#1F3A5F] transition-colors">Request viewing →</button>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* RECENT CLOSINGS — brass-on-ink marquee table */}
      <section className="bg-[#0E1B2D] text-[#F4EFE5] py-12 mt-12">
        <div className="max-w-[1500px] mx-auto px-8 lg:px-14">
          <div className="grid md:grid-cols-[260px_1fr] gap-10 items-start">
            <div>
              <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#C2A36B] mb-3">— Notable closings</p>
              <p className="font-serif italic text-3xl text-[#F4EFE5]">A discreet<br />ledger.</p>
            </div>
            <div className="divide-y divide-[#F4EFE5]/15">
              {RECENT_SALES.map(s => (
                <div key={s.year + s.loc} className="grid grid-cols-[80px_1fr_auto] gap-6 py-3.5 items-baseline">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C2A36B]">{s.year}</p>
                  <p className="font-serif text-lg text-[#F4EFE5]">{s.loc}</p>
                  <p className="font-mono text-sm tracking-[0.1em] text-[#F4EFE5]/85">{s.price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
